import {
  CreateChatRequest,
  RefinePromptInput,
  ScoreAnswerRequest,
  UpdateChatBody,
} from '@ditto/shared';
import {
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ApiError, useAuthenticatedClient, useProtectedQuery } from './useApi';

export const queryKeys = {
  all: ['chats'],
  allChats: () => [...queryKeys.all, 'chats'],
  chats: (page: number, limit: number, searchString?: string) => [
    ...queryKeys.allChats(),
    page,
    limit,
    searchString,
  ],
  messages: (chatId: string) => [...queryKeys.all, 'messages', chatId],
  chat: (chatId: string) => [...queryKeys.all, 'chat', chatId],
  chatConfig: (chatId: string) => [...queryKeys.all, 'chatConfig', chatId],
};

export function useMessages(chatId: string) {
  const { client } = useAuthenticatedClient();
  return useProtectedQuery({
    queryKey: queryKeys.messages(chatId),
    queryFn: async () => {
      const response = await client.chat.getMessages({
        params: { id: chatId },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch messages');
    },
  });
}

export function useChats(
  page: number = 1,
  limit: number = 10,
  searchString?: string
) {
  const { client } = useAuthenticatedClient();
  return useProtectedQuery({
    queryKey: queryKeys.chats(page, limit, searchString),
    queryFn: async () => {
      const response = await client.chat.getChats({
        query: {
          page,
          limit,
          searchString,
        },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch chats');
    },
  });
}

export function useChatConfig(
  chatId: string,
  options?: Omit<UseQueryOptions<any, Error, any, any>, 'queryKey' | 'queryFn'>
) {
  const { client } = useAuthenticatedClient();
  return useProtectedQuery({
    queryKey: queryKeys.chatConfig(chatId),
    queryFn: async () => {
      const response = await client.chat.getChatConfig({
        params: { id: chatId },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch chat config');
    },
    ...options,
  });
}

export function useChatById(chatId: string) {
  const { client } = useAuthenticatedClient();
  return useProtectedQuery({
    queryKey: queryKeys.chat(chatId),
    queryFn: async () => {
      try {
        const response = await client.chat.getChatById({
          params: { id: chatId },
        });
        if (response.status === 200) {
          return response.body;
        }
        throw new Error('Failed to fetch chat');
      } catch (error) {
        // Chat doesn't exist yet (will be created on first message)
        // This is expected for new chats, so return null instead of throwing
        if (error instanceof ApiError && error.statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!chatId,
    retry: false, // Don't retry on 404
  });
}

export function useCreateChat() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateChatRequest) => {
      const response = await client.chat.createChat({ body: data });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create chat');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });
}

export function useDeleteChat(chatId: string) {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await client.chat.deleteChat({ params: { id: chatId } });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete chat');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
    onError: error => {
      console.error('Failed to delete chat:', error);
    },
  });
}

export function useUpdateChatApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      chatId,
      data,
    }: {
      chatId: string;
      data: UpdateChatBody;
    }) => {
      const response = await client.chat.updateChat({
        params: { id: chatId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update chat');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export const useRefinePromptApi = () => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async (data: RefinePromptInput) => {
      const response = await client.chat.refinePrompt({ body: data });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to refine prompt');
    },
  });
};

export function useScoreAnswerApi() {
  const { client } = useAuthenticatedClient();
  return useMutation({
    mutationFn: async (data: ScoreAnswerRequest) => {
      const response = await client.chat.scoreResponse({
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to score response');
    },
  });
}
